import {
    billingApi,
    type Balance,
    type BalanceTransaction,
    type BalanceTransactionsResponse,
    type CreateServicePaymentRequest,
    type CreateTopUpRequest,
    type CryptoCurrency,
    type CryptoEstimate,
    type MinAmountResponse,
    type RenewalInfo,
    type ServicePayment,
    type TopUpSession,
} from '@/api/billing'
import { useTeamStore } from '@/store/team'
import { getErrorMessage } from '@/utils/error-handling'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

// Re-export types
export type {
    Balance,
    BalanceTransaction,
    BalanceTransactionsResponse,
    CryptoCurrency,
    CryptoEstimate,
    MinAmountResponse,
    RenewalInfo,
    ServicePayment,
    TopUpSession
}

// =============================================================================
// Query Keys
// =============================================================================

export const balanceKeys = {
  all: ['balance'] as const,
  balance: () => [...balanceKeys.all, 'current'] as const,
  transactions: (limit?: number, offset?: number) =>
    [...balanceKeys.all, 'transactions', { limit, offset }] as const,
  currencies: () => [...balanceKeys.all, 'currencies'] as const,
  estimate: (amountUsd: number, currency: string) =>
    [...balanceKeys.all, 'estimate', { amountUsd, currency }] as const,
  minAmount: (currency: string) => [...balanceKeys.all, 'min-amount', currency] as const,
  topUpSession: (id: string) => [...balanceKeys.all, 'topup', id] as const,
  activeTopUps: () => [...balanceKeys.all, 'topup', 'active'] as const,
  servicePayments: () => [...balanceKeys.all, 'service-payments'] as const,
  servicePayment: (id: string) => [...balanceKeys.all, 'service-payments', id] as const,
  renewalInfo: () => [...balanceKeys.all, 'renewal-info'] as const,
}

// =============================================================================
// Balance Hooks
// =============================================================================

/** Current user balance — auto-refreshes on WebSocket `billing.balance_updated` */
export function useBalance() {
  const currentTeamId = useTeamStore((s) => s.currentTeamId)
  return useQuery({
    queryKey: balanceKeys.balance(),
    queryFn: () => billingApi.getBalance(),
    enabled: !!currentTeamId,
    staleTime: 30 * 1000, // 30 seconds
  })
}

/** Paginated balance transaction history */
export function useBalanceTransactions(limit = 20, offset = 0) {
  return useQuery({
    queryKey: balanceKeys.transactions(limit, offset),
    queryFn: () => billingApi.getBalanceTransactions({ limit, offset }),
    staleTime: 30 * 1000,
  })
}

// =============================================================================
// Crypto Currency Hooks
// =============================================================================

/** Available crypto currencies — cached 5 minutes */
export function useCurrencies() {
  return useQuery({
    queryKey: balanceKeys.currencies(),
    queryFn: () => billingApi.getCurrencies(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/** Debounced estimate: crypto amount for given USD + currency */
export function useEstimate(amountUsd: number, currency: string) {
  return useQuery({
    queryKey: balanceKeys.estimate(amountUsd, currency),
    queryFn: () => billingApi.getEstimate(amountUsd, currency),
    enabled: amountUsd >= 5 && currency.length > 0,
    staleTime: 30 * 1000, // Rates change frequently
    refetchInterval: 30 * 1000, // Refresh every 30s while active
  })
}

/** Minimum payment amount for a currency */
export function useMinAmount(currency: string) {
  return useQuery({
    queryKey: balanceKeys.minAmount(currency),
    queryFn: () => billingApi.getMinAmount(currency),
    enabled: currency.length > 0,
    staleTime: 5 * 60 * 1000,
  })
}

// =============================================================================
// Top-Up Hooks
// =============================================================================

/** Create a new top-up session */
export function useCreateTopUp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CreateTopUpRequest) => billingApi.createTopUp(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: balanceKeys.activeTopUps() })
      toast.success('Top-up session created', {
        description: 'Send the crypto payment to the displayed address.',
      })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

/** Poll a specific top-up session status — every 10s + WebSocket updates */
export function useTopUpSession(sessionId: string) {
  return useQuery({
    queryKey: balanceKeys.topUpSession(sessionId),
    queryFn: () => billingApi.getTopUpSession(sessionId),
    enabled: !!sessionId,
    refetchInterval: 10 * 1000, // Poll every 10s
    staleTime: 5 * 1000,
  })
}

/** List active (pending) top-up sessions */
export function useActiveTopUps() {
  return useQuery({
    queryKey: balanceKeys.activeTopUps(),
    queryFn: () => billingApi.getActiveTopUps(),
    staleTime: 30 * 1000,
  })
}

// =============================================================================
// Service Payment Hooks
// =============================================================================

/** Create a service payment (domain purchase, renewal, etc.) */
export function useCreateServicePayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CreateServicePaymentRequest) => billingApi.createServicePayment(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: balanceKeys.balance() })
      queryClient.invalidateQueries({ queryKey: balanceKeys.servicePayments() })
      queryClient.invalidateQueries({ queryKey: balanceKeys.transactions() })
      if (data.status === 'completed') {
        toast.success('Payment completed', { description: data.description })
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

/** List all service payments */
export function useServicePayments() {
  return useQuery({
    queryKey: balanceKeys.servicePayments(),
    queryFn: () => billingApi.getServicePayments(),
    staleTime: 60 * 1000,
  })
}

/** Get specific service payment */
export function useServicePayment(id: string) {
  return useQuery({
    queryKey: balanceKeys.servicePayment(id),
    queryFn: () => billingApi.getServicePayment(id),
    enabled: !!id,
  })
}

// =============================================================================
// Renewal Info
// =============================================================================

/** Subscription renewal status — next date, amount, balance sufficient */
export function useRenewalInfo() {
  const currentTeamId = useTeamStore((s) => s.currentTeamId)
  return useQuery({
    queryKey: balanceKeys.renewalInfo(),
    queryFn: () => billingApi.getRenewalInfo(),
    enabled: !!currentTeamId,
    staleTime: 60 * 1000,
  })
}
